/**
 * CATIRA NO APARELHO (D3) — os movimentos do estoque do gestor e a fila deles.
 *
 * Mesmo jeito do extrato: guarda no localStorage, por pessoa (a chave leva o
 * id de quem entrou), e o que foi lançado aqui fica PENDENTE até o
 * «Sincronizar». Sem sinal funciona: o gestor lança recria, morte e contagem
 * no curral, e tudo sobe no botão.
 *
 * Só o gestor lança; a Consulta só recebe; o comprador não recebe nada (o
 * servidor recusa, e aqui a recusa dele é silenciosa — a catira nem existe
 * para ele).
 *
 * Nada se apaga: movimento errado é ANULADO com motivo, e a anulação também
 * entra na fila. O que o servidor recusa de vez sai da fila e fica aqui
 * marcado «recusado pelo servidor», riscado e fora das contas.
 *
 * Ao mudar o formato dos dados guardados, mudar a versão da CHAVE.
 */
import { useSyncExternalStore } from 'react'
import { lerDono, lerEstado } from './armazem'
import { api, Recusa } from './sessao'
import { categoriaDoMovimento, type Movimento } from '../dominio/catira'

// tipos e contas moram em src/dominio/catira.ts (testados em testes/contas.mjs)
export type {
  AlvoDaFicha, AlvoPorCategoria, Calculo, Diferenca, DiferencaDaCategoria, Efeito, Movimento, ParteDaCategoria, PesosDaCategoria,
  ResultadoDaCategoria, Resultados, Saldo, SaldoDaCategoria, TipoDeMovimento,
} from '../dominio/catira'
export {
  abasDaCatira, alvoDaFicha, alvoPorCategoria, CATEGORIA_GERAL, CATEGORIAS_DE_RECRIA, categoriaDoMovimento, categoriaNoEstoque,
  categoriasComAlgo, diferencaDaFicha, diferencasPorCategoria, ESTOQUE_PRINCIPAL, estoqueAntes, jaLancadoDaFicha,
  jaLancadoPorCategoria, juntarPorCategoria, NOME_DO_TIPO, pesosPorCategoria, RATEIO, recalcular, resultadosDoPeriodo,
} from '../dominio/catira'

export interface EstadoDaCatira {
  movimentos: Movimento[]
  /** ids de movimentos lançados ou anulados aqui que ainda não subiram */
  pendentes: string[]
  ultimaSync: string | null
}

const PREFIXO = 'pesagem.catira.v1.'

function vazio(): EstadoDaCatira {
  return { movimentos: [], pendentes: [], ultimaSync: null }
}

function completar(e: Partial<EstadoDaCatira> | null): EstadoDaCatira {
  if (!e || typeof e !== 'object') return vazio()
  return {
    movimentos: Array.isArray(e.movimentos) ? e.movimentos : [],
    pendentes: Array.isArray(e.pendentes) ? e.pendentes : [],
    ultimaSync: e.ultimaSync ?? null,
  }
}

let donoAberto: string | null = null
let estado = vazio()
const ouvintes = new Set<() => void>()

/** Os dados são de quem entrou: confere a cada leitura e troca quando a pessoa mudou. */
function garantirDono(): void {
  const id = lerDono()?.id ?? null
  if (id === donoAberto) return
  donoAberto = id
  try {
    const t = id ? localStorage.getItem(PREFIXO + id) : null
    estado = completar(t ? JSON.parse(t) : null)
  } catch {
    estado = vazio()
  }
}

export function usarCatira(): EstadoDaCatira {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => { garantirDono(); return estado },
  )
}

export function lerCatira(): EstadoDaCatira {
  garantirDono()
  return estado
}

/** Toda alteração passa por aqui. */
function mudarCatira(fn: (e: EstadoDaCatira) => void): void {
  garantirDono()
  const novo = structuredClone(estado)
  fn(novo)
  estado = novo
  try { if (donoAberto) localStorage.setItem(PREFIXO + donoAberto, JSON.stringify(estado)) } catch { /* sem disco: a tela das fichas já avisa */ }
  ouvintes.forEach((f) => f())
}

/* ---------- lançar e anular (com ou sem sinal) — só o gestor chega aqui ---------- */

/** Lança um ou mais movimentos juntos (ex.: as cabeças encontradas e a venda). */
export function lancarMovimentos(...ms: Movimento[]): void {
  mudarCatira((e) => {
    for (const m of ms) {
      e.movimentos.push(m)
      if (!e.pendentes.includes(m.id)) e.pendentes.push(m.id)
    }
  })
}

export function anularMovimento(id: string, motivo: string, porId: string): void {
  mudarCatira((e) => {
    const m = e.movimentos.find((x) => x.id === id)
    if (!m || m.anulacao || m.recusa) return
    m.anulacao = { em: new Date().toISOString(), por: porId, motivo }
    if (!e.pendentes.includes(id)) e.pendentes.push(id)
  })
}

/* ---------- sincronizar (chamado pelo «Sincronizar» de fila.ts) ---------- */

export interface ContagemDaCatira { subiram: number; desceram: number; recusas: string[] }

/**
 * Sobe os pendentes e desce o que mudou. Sem sinal: lança `SemSinal` (a fila
 * para tudo, nada se perde). Recusa do servidor vira frase na tela e NÃO
 * derruba a sincronização das fichas — inclusive se o banco ainda não tiver
 * a migração 008.
 */
export async function sincronizarCatira(r: ContagemDaCatira): Promise<void> {
  if (!lerDono()) return
  try {
    await subir(r)
    await descer(r)
  } catch (e) {
    if (e instanceof Recusa) {
      // o comprador não vê a catira: a recusa dele não é aviso, e nada fica aqui
      if (e.status === 403) { mudarCatira((x) => { x.movimentos = []; x.pendentes = []; x.ultimaSync = null }); return }
      r.recusas.push(`Catira: ${e.message}`)
      return
    }
    throw e
  }
}

/**
 * «a ficha ainda não chegou ao banco»: fica na fila, sem alarde — salvo se a
 * ficha foi RECUSADA pelo servidor neste aparelho (R1A, contador preso): aí
 * ela nunca chega, e o movimento é recusa de vez.
 */
function esperar(e: Recusa, m: Movimento): boolean {
  if (!(e.status === 409 && e.dados?.['erro'] === 'ficha_ausente')) return false
  const est = lerEstado()
  return !(m.fichaId && m.fichaId in est.recusadas.fichas && !est.pendentes.fichas.includes(m.fichaId))
}

/** Recusa definitiva: 4xx que não seja 401 nem a espera acima. 5xx tenta de novo. */
function deVez(e: Recusa, m: Movimento): boolean {
  return !esperar(e, m) && e.status >= 400 && e.status < 500 && e.status !== 401
}

/**
 * R1A · «Sair»: fica só o que ainda não subiu (nada se perde); a catira que
 * já está no banco sai do aparelho e volta inteira no próximo «Sincronizar».
 */
export function enxugarCatiraAoSair(): void {
  garantirDono()
  if (!donoAberto) return
  mudarCatira((e) => {
    e.movimentos = e.movimentos.filter((m) => e.pendentes.includes(m.id))
    e.ultimaSync = null
  })
}

function dataBR(d: string): string {
  return d.split('-').reverse().join('/')
}

async function subir(r: ContagemDaCatira): Promise<void> {
  for (const id of [...lerCatira().pendentes]) {
    const m = lerCatira().movimentos.find((x) => x.id === id)
    if (!m) { tirarDaFila(id); continue }
    try {
      await api('catira/movimentos', {
        id: m.id, estoque_id: m.estoqueId, tipo: m.tipo, data: m.data, em: m.em, cabecas: m.cabecas, contadas: m.contadas,
        valor: m.valor, categoria: m.categoria, descricao: m.descricao, ficha_id: m.fichaId,
        // R2D (migração 015): movimento lançado antes desta versão não tem categoria → «Geral»
        categoria_gado: categoriaDoMovimento(m), kg: m.kg ?? null, arrobas: m.arrobas ?? null,
        anulacao: m.anulacao ? { motivo: m.anulacao.motivo } : undefined,
      })
      mudarCatira((e) => {
        // só sai da fila se ninguém anulou enquanto subia
        const x = e.movimentos.find((y) => y.id === id)
        if (!x || !!x.anulacao === !!m.anulacao) e.pendentes = e.pendentes.filter((y) => y !== id)
      })
      r.subiram++
    } catch (e) {
      if (!(e instanceof Recusa)) throw e
      if (esperar(e, m)) continue
      const motivo = e.dados?.['erro'] === 'ficha_ausente' ? 'a ficha foi recusada pelo servidor e não vai chegar ao banco.' : e.message
      const frase = `Catira, ${NOME_CURTO[m.tipo]} de ${dataBR(m.data)}: ${motivo}`
      if (e.status === 409 && e.dados?.['codigo'] === 'nao_anulou') {
        // a anulação não valeu: o movimento volta a valer aqui
        mudarCatira((x) => {
          const y = x.movimentos.find((z) => z.id === id)
          if (y) y.anulacao = null
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
        r.recusas.push(frase)
        continue
      }
      if (deVez(e, m)) {
        // Mandar de novo daria a mesma resposta para sempre: o contador nunca
        // zeraria e a versão nova do app nunca seria oferecida. Sai da fila e
        // fica aqui riscado, fora das contas — nada se apaga.
        mudarCatira((x) => {
          const y = x.movimentos.find((z) => z.id === id)
          if (y) y.recusa = { em: new Date().toISOString(), motivo }
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
        r.recusas.push(`${frase} — não entrou na catira.`)
        continue
      }
      r.recusas.push(frase)
    }
  }
}

const NOME_CURTO: Record<Movimento['tipo'], string> = {
  entrada: 'entrada', ajuste_entrada: 'ajuste de compra', recria: 'recria', morte: 'morte',
  venda: 'venda', ajuste_venda: 'ajuste de venda', contagem: 'contagem',
}

async function descer(r: ContagemDaCatira): Promise<void> {
  const desde = lerCatira().ultimaSync
  const q = desde ? `?desde=${encodeURIComponent(desde)}` : ''
  const d = await api<{ agora: string; movimentos: Movimento[] }>(`catira${q}`)
  mudarCatira((e) => {
    for (const novo of d.movimentos ?? []) {
      if (e.pendentes.includes(novo.id)) continue // a mudança daqui ainda vai subir
      const i = e.movimentos.findIndex((x) => x.id === novo.id)
      if (i >= 0) {
        const antes = e.movimentos[i]!
        if (!!antes.anulacao !== !!novo.anulacao || antes.recusa) r.desceram++
        e.movimentos[i] = novo
      } else {
        e.movimentos.push(novo)
        r.desceram++
      }
    }
    e.ultimaSync = d.agora || e.ultimaSync
  })
}

function tirarDaFila(id: string): void {
  mudarCatira((e) => { e.pendentes = e.pendentes.filter((y) => y !== id) })
}
